// Full-screen Eden conversation: viewport fit + background scroll control.
// PARITY: byte-identical between client and superadmin.
//
// While `active`:
//  - the sheet element gets --eden-chat-vv-top / --eden-chat-vv-height from
//    window.visualViewport, so the composer stays above the on-screen
//    keyboard (iOS shrinks the VISUAL viewport and may pan it; a plain
//    `position: fixed; inset: 0` sheet would leave the composer under the
//    keyboard);
//  - <html> gets `cphere-eden-chat-fullscreen-open`, whose CSS stops the page
//    behind the sheet from scrolling. The page's own layout (width, scale,
//    grid) is never touched: no width, position or transform is written to
//    html, body or #root.
// On release the class is removed and the page's scroll position is restored
// if the keyboard or the browser moved it — unless the route changed while
// the sheet was open, in which case the new page keeps its own position.
// Locks are counted, so overlapping holders never leave a stale lock.

import { useLayoutEffect } from "react";

export const EDEN_FULLSCREEN_HTML_CLASS = "cphere-eden-chat-fullscreen-open";
export const EDEN_FULLSCREEN_GUTTER_CLASS = "cphere-eden-chat-fullscreen-gutter";

let lockCount = 0;

function currentPath() {
  try {
    return `${window.location.pathname}${window.location.search}`;
  } catch {
    return "";
  }
}

export function acquireEdenFullscreenLock() {
  const root = document.documentElement;
  const saved = { x: window.scrollX || 0, y: window.scrollY || 0, path: currentPath() };
  lockCount += 1;
  root.classList.add(EDEN_FULLSCREEN_HTML_CLASS);
  // Where the page shows a classic scrollbar, keep its gutter while the page
  // cannot scroll, so the page behind the sheet keeps its exact width.
  const hadScrollbar = window.innerWidth - root.clientWidth > 0;
  if (hadScrollbar) root.classList.add(EDEN_FULLSCREEN_GUTTER_CLASS);
  let released = false;
  return function release() {
    if (released) return;
    released = true;
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      root.classList.remove(EDEN_FULLSCREEN_HTML_CLASS);
      root.classList.remove(EDEN_FULLSCREEN_GUTTER_CLASS);
    }
    if (currentPath() !== saved.path) return;
    if ((window.scrollX || 0) !== saved.x || (window.scrollY || 0) !== saved.y) {
      try {
        window.scrollTo(saved.x, saved.y);
      } catch {
        // jsdom / very old engines: nothing to restore.
      }
    }
  };
}

export function readEdenSheetViewportBox() {
  const vv = window.visualViewport;
  const height = vv && Number.isFinite(vv.height) && vv.height > 0 ? vv.height : window.innerHeight;
  const top = vv && Number.isFinite(vv.offsetTop) ? Math.max(0, vv.offsetTop) : 0;
  return { top: Math.round(top), height: Math.round(height) };
}

export default function useEdenFullscreenSheet(active, sheetRef) {
  useLayoutEffect(() => {
    if (!active) return undefined;
    const release = acquireEdenFullscreenLock();
    const vv = window.visualViewport;
    // The sheet element for this open: the panel is rendered in the same
    // commit that makes the sheet active.
    const sheet = sheetRef?.current || null;
    let frame = 0;

    const apply = () => {
      frame = 0;
      const el = sheet;
      if (!el) return;
      const box = readEdenSheetViewportBox();
      el.style.setProperty("--eden-chat-vv-top", `${box.top}px`);
      el.style.setProperty("--eden-chat-vv-height", `${box.height}px`);
      // An on-screen keyboard takes far more than browser chrome does.
      el.dataset.edenKeyboard = window.innerHeight - box.height > 120 ? "open" : "closed";
    };
    const schedule = () => {
      if (frame) return;
      frame = window.requestAnimationFrame ? window.requestAnimationFrame(apply) : (apply(), 0);
    };

    apply();
    vv?.addEventListener?.("resize", schedule);
    vv?.addEventListener?.("scroll", schedule);
    window.addEventListener("resize", schedule);
    window.addEventListener("orientationchange", schedule);

    return () => {
      if (frame && window.cancelAnimationFrame) window.cancelAnimationFrame(frame);
      vv?.removeEventListener?.("resize", schedule);
      vv?.removeEventListener?.("scroll", schedule);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("orientationchange", schedule);
      const el = sheet;
      if (el) {
        el.style.removeProperty("--eden-chat-vv-top");
        el.style.removeProperty("--eden-chat-vv-height");
        delete el.dataset.edenKeyboard;
      }
      release();
    };
  }, [active, sheetRef]);
}
